import z from "zod";
import type { PartnerAccountId } from "@remi/enums/partner-account-id";
import type { PartnerPhase } from "@remi/enums/partner-phase";
import type { PortalScreenshotRefusalReason } from "@remi/types/automation-result";

import { ErrorClassification } from "../../../sentry/classification";
import type { ErrorDetails } from "../domain";

const bidErrorTypeValues = [
  "UNKNOWN",
  "BID_NOT_FOUND",
  "REQUEST_FOR_QUOTE_NOT_FOUND",
  "BID_REQUEST_NOT_FOUND",
  "QUOTE_CALCULATION_ERROR",
  "MERGED_BID_CONFLICT",
  "MISSING_ORGANIZATIONS",
  "BID_FINALIZATION_EMIT_FAILED",
  "NO_BID_REQUESTS_FOUND",
  "INVALID_BID_STATE",
  "DECLINE_REASON_REQUIRED",
  "INVALID_WORK_SITE",
  "ESTIMATE_APPROVAL_FAILED",
  "ESTIMATE_CANCELLATION_FAILED",
  "ESTIMATE_CORRECTION_FAILED",
  "EXTERNAL_QUOTE_NOT_FOUND",
  "IMAGE_FETCH_FAILED",
  "CENTERPOINT_NOT_FOUND",
  "COORDINATES_NOT_FOUND",
  "INSURANCE_MULTIPLE_RFQ",
  "POST_PTO_SINGLE_RFQ_VIOLATION",
  "TASK_NOT_FOUND_AFTER_ATTEMPTS",
  "SEED_BID_FORBIDDEN_IN_PRODUCTION",
  "TASK_NOT_FOUND",
  "WORK_ORDER_NOT_FOUND",
  "NO_ACTIVE_WORKFLOW",
  "REGION_NOT_FOUND",
  "BID_REQUEST_ALREADY_EXISTS",
  "BATCH_SUBMIT_REQUEST_FOR_QUOTES_FAILED",
  "FAILED_TO_UPDATE_PROJECT_PHASE",
  "FAILED_TO_MOVE_BID_REQUESTS",
  "FAILED_TO_MOVE_REQUESTS_FOR_QUOTE",
  "CONFLICTING_INSTALL_SCHEDULED_DATES",
  "MULTIPLE_OPEN_STRUCTURAL_PROJECTS",
  "MULTIPLE_ACTIVE_NON_STRUCTURAL_PROJECTS",
  "QUOTE_VERSION_NOT_FOUND",
  "INVALID_STATE_FOR_REMI_PAPER",
  "WORKSITE_NOT_FOUND",
  "INSURANCE_ESTIMATE_STATUS_NOT_FOUND",
  "BID_APPROVAL_NOT_FOUND",
  "FAILED_TO_EVALUATE_MODEL_FEATURES",
  "INVALID_PARTNER_PHASE",
  "ESTIMATE_NOT_FOUND",
  "ADJUSTER_NOT_FOUND",
  "ADJUSTER_HAS_NO_EMAIL",
  "AMBIGUOUS_QUOTE_IDENTIFIER",
  "BID_REQUEST_MISSING_JOB_CODE",
  "PAYMENT_METHOD_LOCKED_AFTER_HIC_SIGNED",
  // Unified shape for "bid missing OR cross-org" — used by MCP / agent-action authorization.
  // Returning the same errorType for both cases prevents an existence-oracle leak that would
  // let an attacker probe org boundaries by submitting cross-org bidIds.
  "BID_NOT_ACCESSIBLE",
  "MOVE_BID_REQUEST_NO_OP",
  "BID_NOT_MERGED",
  "ROUND_ROBIN_RATES_EXCEED_100",
  "REQUEST_FOR_QUOTE_REJECTED",
  "PORTAL_SCREENSHOT_REFRESH_REFUSED",
  "PORTAL_SCREENSHOT_REFRESH_FAILED",
  "REVIEW_BID_GUIDE_INVALID",
  "REVIEW_BID_GUIDE_NO_DRAFT",
  "REVIEW_BID_GUIDE_STALE_EDIT",
] as const;

const bidErrorTypeSchema = z.enum(bidErrorTypeValues);
export const BidErrorType = bidErrorTypeSchema.enum;
type BidErrorType = z.infer<typeof bidErrorTypeSchema>;

export type BidErrors =
  | { errorType: typeof BidErrorType.UNKNOWN; data: undefined }
  | { errorType: typeof BidErrorType.BID_NOT_FOUND; data: { bidId?: number; projectId?: number } }
  | { errorType: typeof BidErrorType.REQUEST_FOR_QUOTE_NOT_FOUND; data: { bidId?: number; requestForQuoteId?: number } }
  | {
      errorType: typeof BidErrorType.BID_REQUEST_NOT_FOUND;
      data: {
        requestForQuoteId?: number;
        id?: string;
        projectId?: number;
        bidRequestId?: string;
        pvSystemId?: string;
        availableBidRequests?: number;
        externalId?: string;
        organizationId?: string;
        buyerOrganizationId?: string;
      };
    }
  | { errorType: typeof BidErrorType.INVALID_WORK_SITE; data: { worksiteId: number } }
  | { errorType: typeof BidErrorType.QUOTE_CALCULATION_ERROR; data: { errors: string[] } }
  | { errorType: typeof BidErrorType.MERGED_BID_CONFLICT; data: { newBidId: number; mergedBids: number[] } }
  | {
      errorType: typeof BidErrorType.MISSING_ORGANIZATIONS;
      data: {
        bidId: number;
        contractorOrganizationId?: string;
        buyerOrganizationId?: string;
        homeownerOrganizationId?: string;
      };
    }
  | {
      errorType: typeof BidErrorType.BID_FINALIZATION_EMIT_FAILED;
      data: { bidProjectId: number; originalErrorName: string; originalErrorMessage: string };
    }
  | {
      errorType: typeof BidErrorType.NO_BID_REQUESTS_FOUND;
      data: { bidId: number };
    }
  | {
      errorType: typeof BidErrorType.INVALID_BID_STATE;
      data: { bidId: number; currentState?: string };
    }
  | { errorType: typeof BidErrorType.DECLINE_REASON_REQUIRED; data: undefined }
  | {
      errorType: typeof BidErrorType.ESTIMATE_APPROVAL_FAILED;
      data: { bidRequestId: string };
    }
  | {
      errorType: typeof BidErrorType.ESTIMATE_CANCELLATION_FAILED;
      data: { bidRequestId: string };
    }
  | {
      errorType: typeof BidErrorType.ESTIMATE_CORRECTION_FAILED;
      data: { bidRequestId: string };
    }
  | {
      errorType: typeof BidErrorType.EXTERNAL_QUOTE_NOT_FOUND;
      data: { bidRequestId: string };
    }
  | { errorType: typeof BidErrorType.IMAGE_FETCH_FAILED; data: { url: string; response: Response } }
  | {
      errorType: typeof BidErrorType.CENTERPOINT_NOT_FOUND;
      data: { requestForQuoteId: number };
    }
  | {
      errorType: typeof BidErrorType.COORDINATES_NOT_FOUND;
      data: {
        requestForQuoteId: number;
        longitude?: number;
        latitude?: number;
      };
    }
  | {
      errorType: typeof BidErrorType.INSURANCE_MULTIPLE_RFQ;
      data: { bidId: number };
    }
  | {
      errorType: typeof BidErrorType.POST_PTO_SINGLE_RFQ_VIOLATION;
      data: { bidId: number };
    }
  | {
      errorType: typeof BidErrorType.TASK_NOT_FOUND_AFTER_ATTEMPTS;
      data: { taskType: string; projectId: number };
    }
  | {
      errorType: typeof BidErrorType.SEED_BID_FORBIDDEN_IN_PRODUCTION;
      data: Record<string, never>;
    }
  | {
      errorType: typeof BidErrorType.TASK_NOT_FOUND;
      data: { taskType: string; projectId: number };
    }
  | {
      errorType: typeof BidErrorType.WORK_ORDER_NOT_FOUND;
      data: { projectId: number };
    }
  | { errorType: typeof BidErrorType.NO_ACTIVE_WORKFLOW; data: undefined }
  | {
      errorType: typeof BidErrorType.REGION_NOT_FOUND;
      data: { projectId: number };
    }
  | {
      errorType: typeof BidErrorType.BID_REQUEST_ALREADY_EXISTS;
      data: { externalId?: string; organizationId: string };
    }
  | {
      errorType: typeof BidErrorType.BATCH_SUBMIT_REQUEST_FOR_QUOTES_FAILED;
      data: { failedExternalIds: (string | undefined)[] };
    }
  | {
      errorType: typeof BidErrorType.FAILED_TO_UPDATE_PROJECT_PHASE;
      data: { projectId: number; failedProjectIds: number[] };
    }
  | {
      errorType: typeof BidErrorType.FAILED_TO_MOVE_BID_REQUESTS;
      data: { failedBidRequestIds: string[] };
    }
  | {
      errorType: typeof BidErrorType.FAILED_TO_MOVE_REQUESTS_FOR_QUOTE;
      data: { failedRequestForQuoteIds: number[] };
    }
  | {
      errorType: typeof BidErrorType.CONFLICTING_INSTALL_SCHEDULED_DATES;
      data: { projectIds: number[] };
    }
  | {
      errorType: typeof BidErrorType.MULTIPLE_OPEN_STRUCTURAL_PROJECTS;
      data: { projectIds: number[] };
    }
  | {
      errorType: typeof BidErrorType.MULTIPLE_ACTIVE_NON_STRUCTURAL_PROJECTS;
      data: { projectIds: number[] };
    }
  | {
      errorType: typeof BidErrorType.QUOTE_VERSION_NOT_FOUND;
      data: { quoteVersionId: string };
    }
  | {
      errorType: typeof BidErrorType.INVALID_STATE_FOR_REMI_PAPER;
      data: { stateCode: string; partnerFlow: string; bidId: number };
    }
  | {
      errorType: typeof BidErrorType.WORKSITE_NOT_FOUND;
      data: { worksiteId?: number };
    }
  | {
      errorType: typeof BidErrorType.INSURANCE_ESTIMATE_STATUS_NOT_FOUND;
      data: { id: string };
    }
  | {
      errorType: typeof BidErrorType.BID_APPROVAL_NOT_FOUND;
      data: { externalId: string };
    }
  | {
      errorType: typeof BidErrorType.FAILED_TO_EVALUATE_MODEL_FEATURES;
      data: { bidId: number };
    }
  | {
      errorType: typeof BidErrorType.INVALID_PARTNER_PHASE;
      data: { requestForQuoteId: number; partnerPhase?: PartnerPhase };
    }
  | {
      errorType: typeof BidErrorType.ESTIMATE_NOT_FOUND;
      data: { requestForQuoteId: number };
    }
  | {
      errorType: typeof BidErrorType.ADJUSTER_NOT_FOUND;
      data: { rfqId: number; projectId: number };
    }
  | {
      errorType: typeof BidErrorType.ADJUSTER_HAS_NO_EMAIL;
      data: { rfqId: number; projectId: number; adjusterId: string };
    }
  | {
      errorType: typeof BidErrorType.AMBIGUOUS_QUOTE_IDENTIFIER;
      data: { bidQuoteId: string; quoteVersionId: string };
    }
  | {
      errorType: typeof BidErrorType.BID_REQUEST_MISSING_JOB_CODE;
      data: {
        externalId: string;
        organizationId: string;
        partnerAccountId: PartnerAccountId;
        bidRequestType: string;
      };
    }
  | {
      errorType: typeof BidErrorType.PAYMENT_METHOD_LOCKED_AFTER_HIC_SIGNED;
      data: {
        bidId: number;
        requestForQuoteId: number;
        priorPaymentMethod: string | null;
        attemptedPaymentMethod: string;
      };
    }
  | {
      errorType: typeof BidErrorType.BID_NOT_ACCESSIBLE;
      // Batch callers report a count, never the offending ids: naming which of the
      // submitted bids was unreachable rebuilds the existence oracle this error exists
      // to prevent.
      data: { bidId: number } | { bidCount: number };
    }
  | {
      errorType: typeof BidErrorType.MOVE_BID_REQUEST_NO_OP;
      data: { bidRequestId: string; requestForQuoteId: number };
    }
  | {
      errorType: typeof BidErrorType.BID_NOT_MERGED;
      data: { bidId: number };
    }
  | {
      errorType: typeof BidErrorType.ROUND_ROBIN_RATES_EXCEED_100;
      data: { partnerFlow: string; currentTotal: number; newRate: number };
    }
  | {
      errorType: typeof BidErrorType.REQUEST_FOR_QUOTE_REJECTED;
      data: { requestForQuoteId: number };
    }
  | {
      errorType: typeof BidErrorType.PORTAL_SCREENSHOT_REFRESH_REFUSED;
      data: { bidId: number; taskId: string; reason: PortalScreenshotRefusalReason };
    }
  | {
      errorType: typeof BidErrorType.PORTAL_SCREENSHOT_REFRESH_FAILED;
      data: { bidId: number; taskId: string; automationId: string };
    }
  | { errorType: typeof BidErrorType.REVIEW_BID_GUIDE_INVALID; data: { issues: string[] } }
  | { errorType: typeof BidErrorType.REVIEW_BID_GUIDE_NO_DRAFT; data: undefined }
  | {
      errorType: typeof BidErrorType.REVIEW_BID_GUIDE_STALE_EDIT;
      data: { editedVersion: number; latestVersion: number };
    };

export const bidErrorDetails: Record<BidErrorType, ErrorDetails> = {
  UNKNOWN: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Unknown bid domain error",
  },
  BID_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Bid not found",
  },
  REQUEST_FOR_QUOTE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Request for quote not found",
  },
  BID_REQUEST_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Bid request not found",
  },
  QUOTE_CALCULATION_ERROR: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Quote calculation failed with errors",
  },
  MERGED_BID_CONFLICT: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Cannot merge bid that already has merged bids",
  },
  MISSING_ORGANIZATIONS: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Missing necessary organizations for assessment creation",
  },
  BID_FINALIZATION_EMIT_FAILED: {
    classification: ErrorClassification.DEPENDENCY_ERROR,
    sentryDescription: "Failed to emit bid/finalization.requested; bid was finalized inline as a fallback",
  },
  NO_BID_REQUESTS_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No bid requests found for bid",
  },
  INVALID_BID_STATE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Bid is in an invalid state for the requested operation",
  },
  DECLINE_REASON_REQUIRED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "No reason provided for declining FSDs",
  },
  ESTIMATE_APPROVAL_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Failed to approve estimate",
  },
  ESTIMATE_CANCELLATION_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Failed to cancel estimate",
  },
  ESTIMATE_CORRECTION_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Failed to request corrections for estimate",
  },
  EXTERNAL_QUOTE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No external quote found for bid request",
  },
  IMAGE_FETCH_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Failed to fetch image",
  },
  CENTERPOINT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No centerpoint GeoJSON found for property address",
  },
  COORDINATES_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No longitude or latitude found for property address",
  },
  INSURANCE_MULTIPLE_RFQ: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Insurance bid has multiple RFQ",
  },
  POST_PTO_SINGLE_RFQ_VIOLATION: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Post PTO bid should only have one request for quote",
  },
  TASK_NOT_FOUND_AFTER_ATTEMPTS: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Task not found after multiple attempts",
  },
  SEED_BID_FORBIDDEN_IN_PRODUCTION: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "A seeded bid was requested against production",
  },
  TASK_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Task not found",
  },
  WORK_ORDER_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No active work order found for project",
  },
  NO_ACTIVE_WORKFLOW: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No active workflow found",
  },
  REGION_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No region found for project",
  },
  BID_REQUEST_ALREADY_EXISTS: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Bid request with this external ID already exists for organization",
  },
  BATCH_SUBMIT_REQUEST_FOR_QUOTES_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Failed to batch submit request for quotes",
  },
  FAILED_TO_UPDATE_PROJECT_PHASE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Failed to update project phase",
  },
  FAILED_TO_MOVE_BID_REQUESTS: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Failed to move bid requests",
  },
  FAILED_TO_MOVE_REQUESTS_FOR_QUOTE: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Failed to move requests for quote",
  },
  CONFLICTING_INSTALL_SCHEDULED_DATES: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Multiple projects have conflicting install scheduled dates",
  },
  MULTIPLE_OPEN_STRUCTURAL_PROJECTS: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Multiple open structural work projects found",
  },
  MULTIPLE_ACTIVE_NON_STRUCTURAL_PROJECTS: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Multiple active non-structural projects found",
  },
  QUOTE_VERSION_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Quote version not found",
  },
  INVALID_STATE_FOR_REMI_PAPER: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Invalid state for Remi paper",
  },
  WORKSITE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Worksite not found",
  },
  INSURANCE_ESTIMATE_STATUS_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Insurance estimate status not found",
  },
  BID_APPROVAL_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Bid approval not found",
  },
  FAILED_TO_EVALUATE_MODEL_FEATURES: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Failed to evaluate model features",
  },
  INVALID_PARTNER_PHASE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Invalid partner phase",
  },
  INVALID_WORK_SITE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Invalid work site",
  },
  ESTIMATE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Estimate not found",
  },
  ADJUSTER_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Adjuster not found for request for quote",
  },
  ADJUSTER_HAS_NO_EMAIL: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Adjuster has no email address",
  },
  AMBIGUOUS_QUOTE_IDENTIFIER: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Both bidQuoteId and quoteVersionId were provided — only one is allowed",
  },
  BID_REQUEST_MISSING_JOB_CODE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Sunrun partner task ingested without jobCode",
  },
  PAYMENT_METHOD_LOCKED_AFTER_HIC_SIGNED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Cannot change paymentMethod once HIC is signed",
  },
  BID_NOT_ACCESSIBLE: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Bid is missing or not accessible to the caller's organization",
  },
  MOVE_BID_REQUEST_NO_OP: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Bid request is already attached to the destination RFQ",
  },
  BID_NOT_MERGED: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Bid is not merged — cannot unmerge",
  },
  ROUND_ROBIN_RATES_EXCEED_100: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Round-robin pool assignment rates exceed 100%",
  },
  REQUEST_FOR_QUOTE_REJECTED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Cannot operate on rejected request for quote",
  },
  PORTAL_SCREENSHOT_REFRESH_REFUSED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Sunrun portal screenshot refresh was refused",
  },
  PORTAL_SCREENSHOT_REFRESH_FAILED: {
    classification: ErrorClassification.DEPENDENCY_ERROR,
    sentryDescription: "Sunrun portal screenshot refresh could not be queued",
  },
  REVIEW_BID_GUIDE_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Edited Review Bid guide failed validation",
  },
  REVIEW_BID_GUIDE_NO_DRAFT: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Review Bid guide has no unpublished draft",
  },
  REVIEW_BID_GUIDE_STALE_EDIT: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Review Bid guide changed since the editor loaded it",
  },
};
